import React, { type ChangeEvent, createRef/*, type KeyboardEvent*/ } from "react";
import { RoomType/*, JoinRule */} from "matrix-js-sdk/src/matrix";
import withValidation, { type IFieldState, type IValidationResult } from "../elements/Validation";
import { _t } from "../../../languageHandler";
import { MatrixClientPeg } from "../../../MatrixClientPeg";
import { type IOpts } from "../../../createRoom";
import Field from "../elements/Field";
import RoomAliasField from "../elements/RoomAliasField";
import DialogButtons from "../elements/DialogButtons";
import BaseDialog from "./BaseDialog";
// import { getKeyBindingsManager } from "../../../KeyBindingsManager";
// import { KeyBindingAction } from "../../../accessibility/KeyboardShortcuts";
import { privateShouldBeEncrypted } from "../../../utils/rooms";
import ContactList from "../dth_components/ContactList";
import { IDthContact } from "../../../dth/clientHttp/models/httpClientModels";
import { Member } from "../../../utils/direct-messages";
import MiniAvatarUploader, { AVATAR_SIZE } from "../elements/MiniAvatarUploader";
import { mediaFromMxc } from "../../../customisations/Media";
import Modal from "../../../Modal";
import ErrorDialog from "./ErrorDialog";
import InviteProgressBody from "./InviteProgressBody";

interface IProps {
    type?: RoomType;
    defaultName?: string;
    defaultEncrypted?: boolean;
    onFinished(proceed?: false): void;
    onFinished(proceed: true, opts: IOpts): void;    
}

interface IState {
    roomAvatarMxc?: string | null;
    roomAvatarPreview?: string | null;
    contacts: IDthContact[];
    // /**
    //  * The selected room join rule.
    //  */
    // joinRule: JoinRule;
    /**
     * Indicates whether end-to-end encryption is enabled for the room.
     */
    isEncrypted: boolean;
    /**
     * The room name.
     */
    name: string;
    /**
     * The room topic.
     */
    topic: string;
    /**
     * The room alias.
     */
    alias: string;
    /**
     * Indicates whether federation is disabled for the room.
     */
    noFederate: boolean;
    /**
     * Indicates whether the room name is valid.
     */
    nameIsValid: boolean;
    selectedMembers: Member[];
    busy: boolean;
}

export default class CreateRoomDialog extends React.Component<IProps, IState> {
    private nameField = createRef<Field>();
    private aliasField = createRef<RoomAliasField>();

    public constructor(props: IProps) {
        super(props);
        // let joinRule = JoinRule.Invite; //Forzato solo invito

        const cli = MatrixClientPeg.safeGet();
        this.state = {
            isEncrypted: this.props.defaultEncrypted ?? privateShouldBeEncrypted(cli),
            // joinRule,
            name: this.props.defaultName || "",
            topic: "",
            alias: "",
            noFederate: true, //Forzato utilizzo del dominio //SdkConfig.get().default_federate === false,
            nameIsValid: false,
            contacts: [],
            selectedMembers: [],
            roomAvatarMxc: null,
            roomAvatarPreview: null,
            busy: false
        };
    }

    private roomCreateOptions(): IOpts {
        const opts: IOpts = {};
        const createOpts: IOpts["createOpts"] = (opts.createOpts = {});
        opts.roomType = this.props.type;
        createOpts.name = this.state.name;
        opts.encryption = this.state.isEncrypted;

        if (this.state.topic) {
            createOpts.topic = this.state.topic;
        }

        if (this.state.roomAvatarMxc) {
            createOpts.initial_state = createOpts.initial_state || [];
            createOpts.initial_state.push({
                type: "m.room.avatar",
                state_key: "",
                content: { url: this.state.roomAvatarMxc },
            });
        }

        // aggiungi gli inviti 
        if (this.state.selectedMembers && this.state.selectedMembers.length > 0) {
            // mappa sui userId
            createOpts.invite = this.state.selectedMembers.map((m) => m.userId);
        }

        return opts;
    }

    public componentDidMount(): void {
        this.nameField.current?.focus();
    }

    // private onKeyDown = (event: KeyboardEvent): void => {
    //     const action = getKeyBindingsManager().getAccessibilityAction(event);
    //     switch (action) {
    //         case KeyBindingAction.Enter:
    //             this.onOk();
    //             event.preventDefault();
    //             event.stopPropagation();
    //             break;
    //     }
    // };

    private setRoomAvatarUrl = async (mxcUrl: string): Promise<unknown> => {
        // salva mxc nello stato
        this.setState({ roomAvatarMxc: mxcUrl });

        try {
            const previewHttp = mediaFromMxc(mxcUrl).getSquareThumbnailHttp(parseInt(AVATAR_SIZE, 10));
            this.setState({ roomAvatarPreview: previewHttp });
        } catch (err) {
            // se non riesci a ottenere l'http preview non è critico — lascialo vuoto
            console.warn("Impossibile generare preview avatar da mxc:", err);
            this.setState({ roomAvatarPreview: null });
        }

        return Promise.resolve();
    };

    private renderAvatar(): React.ReactNode {
        return (
            <div className="mx_CreateRoomDialog_avatarRow">
                <MiniAvatarUploader
                    hasAvatar={!!this.state.roomAvatarMxc}
                    hasAvatarLabel={""}
                    noAvatarLabel={""}
                    setAvatarUrl={this.setRoomAvatarUrl}
                    isUserAvatar={true} // fondamentale: non abbiamo ancora una stanza
                >
                    {/* children = contenuto del bottone; qui metti anteprima o placeholder */}
                    {this.state.roomAvatarPreview ? (
                        <img
                            src={this.state.roomAvatarPreview}
                            // alt={_t("create_room.avatar_preview")}
                            style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, objectFit: "cover", borderRadius: 8 }}
                        />
                    ) : (
                        //TODO Aggiungere un immagine
                        <div className="mx_CreateRoomDialog_avatarPlaceholder" aria-hidden="true" role="img" />
                    )}
                </MiniAvatarUploader>
            </div>);
    }

    private onOk = async (): Promise<void> => {
        this.setState({ busy: true });

        if (!this.nameField.current) { this.setState({ busy: false }); return; }
        const activeElement = document.activeElement as HTMLElement;
        activeElement?.blur();
        await this.nameField.current.validate({ allowEmpty: false });
        if (this.aliasField.current) {
            await this.aliasField.current.validate({ allowEmpty: false });
        }
        if (this.state.selectedMembers.length <= 0) {
            this.setState({ busy: false });
            Modal.createDialog(ErrorDialog, {
                title: _t("create_room.error_select_user"),
                description: _t("create_room.error_select_user_description"),
            });
            return;
        }
        await new Promise<void>((resolve) => this.setState({}, resolve));
        if (this.state.nameIsValid && (!this.aliasField.current || this.aliasField.current.isValid)) {
            this.props.onFinished(true, this.roomCreateOptions());
            this.setState({ busy: false });
        } else {
            this.setState({ busy: false }, async () => {
                let field: RoomAliasField | Field | null = null;
                if (!this.state.nameIsValid) {
                    field = this.nameField.current;
                } else if (this.aliasField.current && !this.aliasField.current.isValid) {
                    field = this.aliasField.current;
                }
                if (field) {
                    field.focus();
                    await field.validate({ allowEmpty: false, focused: true });
                }
            });
        }
    };

    private onCancel = (): void => {
        this.props.onFinished(false);
    };

    private onNameChange = (ev: ChangeEvent<HTMLInputElement>): void => {
        this.setState({ name: ev.target.value });
    };

    private onTopicChange = (ev: ChangeEvent<HTMLInputElement>): void => {
        this.setState({ topic: ev.target.value });
    };

    private onNameValidate = async (fieldState: IFieldState): Promise<IValidationResult> => {
        const result = await CreateRoomDialog.validateRoomName(fieldState);
        this.setState({ nameIsValid: !!result.valid });
        return result;
    };

    private static validateRoomName = withValidation({
        rules: [
            {
                key: "required",
                test: async ({ value }) => !!value,
                invalid: () => _t("create_room.name_validation_required"),
            },
        ],
    });

    // Gestisce aggiunta/rimozione membri selezionati
    private onTargetsChange = (targets: Member[]): void => {
        this.setState({ selectedMembers: targets });
    };

    public render(): React.ReactNode {
        const title: string = _t("create_room.title_group");
        return (
            <BaseDialog
                className="mx_InviteDialog_other mx_Dialog_fixedWidth"
                onFinished={this.props.onFinished}
                title={title}
                // screenName="CreateRoom"
            >
                <div className="mx_CreateRoomDialog_row">
                    <div className="mx_CreateRoomDialog_avatarCol">
                        {this.renderAvatar()}
                    </div>

                    <div className="mx_CreateRoomDialog_fieldsCol">
                        <Field
                            ref={this.nameField}
                            label={_t("common.name")}
                            onChange={this.onNameChange}
                            onValidate={this.onNameValidate}
                            value={this.state.name}
                            className="mx_CreateRoomDialog_fieldRow"

                        />

                        <Field
                            label={_t("room_settings.general.topic_field_label")}
                            onChange={this.onTopicChange}
                            value={this.state.topic}
                            className="mx_CreateRoomDialog_fieldRow"
                        />
                    </div>
                </div>

                <div className="mx_InviteDialog_content">
                    {this.state.busy ? <InviteProgressBody /> : <ContactList
                        onTargetsChange={this.onTargetsChange}
                        busy={this.state.busy}
                        allowMultiple={true}
                        showToggle={true}
                    />}
                </div>

                <div className="mx_CreateRoomDialog_buttonsWrap">
                    <DialogButtons
                        primaryButton={_t("create_room.action_create_group")}
                        primaryDisabled={this.state.selectedMembers.length <= 0}
                        onPrimaryButtonClick={this.onOk}
                        onCancel={this.onCancel}
                    />
                </div>

            </BaseDialog>
        );
    }
}
